import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
  Button
} from 'reactstrap';
import config from '../config'; // adjust if needed
import AuthFooter from "components/Footers/AuthFooter";
import AuthNavbar from "components/Navbars/AuthNavbar";
import './OrderSuccess.css'

const OrderSuccess = () => {
  const mainContent = React.useRef(null);
  
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await fetch(
          `${config.apiUrl}/${config.apiStage}/order-success?session_id=${sessionId}`
        );

        if (!response.ok) {
          throw new Error('Failed to retrieve order details');
        }

        const data = await response.json();

        // Transform the data into the expected format
        const orderDetails = {
          orderId: data.metadata.orderId,
          productId: data.metadata.productId,
          selectedOption: data.metadata.selectedOption,
          friendlyName: data.metadata.friendlyName,
          guuid: data.metadata.guuid,
          qrColor: data.metadata.qrColor,
          logoColor: data.metadata.logoColor,
          amountTotal: (data.amount_total / 100).toFixed(2), // Convert cents to dollars
          items: data.line_items.data.map(item => ({
            name: item.description,
            price: (item.amount_total / 100).toFixed(2), // Convert cents to dollars
            quantity: item.quantity,
          })),
          customer: {
            name: data.customer_details.name,
            email: data.customer_details.email,
            shippingAddress: data.customer_details.address,
          },
          paymentStatus: data.payment_status,
          status: data.status,
        };

        setOrderInfo(orderDetails);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      fetchOrder();
    } else {
      setError('Missing session ID in URL');
      setLoading(false);
    }
  }, [sessionId]);

  return (
    <>
      <div>
        <div className="main-content" ref={mainContent}>
          <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
            <AuthNavbar />
            <Container className="mt-5">
              <Row className="justify-content-center">
                <Col md="8">
                  <div className="text-center mt-5 mb-4">
                    <h1>✅ Order Success</h1>
                  </div>

                  {loading && <Spinner />}
                  {error && <Alert color="danger">{error}</Alert>}

                  {orderInfo && (
                    <Container className="mt-5 mb-5 ">
                      {/* Header Section */}
                      <Row className="mb-4">
                        <Col className="text-center">
                          <h2>Thank you for your order!</h2>
                          <p>Your order has been successfully placed. Here are the details:</p>
                        </Col>
                      </Row>
                      <Row className="mb-4">
                      <Col>
                        <h3>
                          Quill: {orderInfo.friendlyName && orderInfo.friendlyName.trim() !== 'empty'
                            ? `'${orderInfo.friendlyName}'`
                            : 'A New Quill'}
                        </h3>
                      </Col>
                    </Row>
                  
                      {/* Order Information Section */}
                      <Row className="mb-4">
                        <Col>
                          <div className="order-summary">
                            <h3>Order Summary</h3>
                            <Table responsive bordered className="order-table">
                              <thead>
                                <tr>
                                  <th>Order ID</th>
                                  <th>Order Date</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>{orderInfo.orderId}</td>
                                  <td>{new Date().toLocaleDateString()}</td> {/* For simplicity, assuming current date */}
                                  <td className="text-success">Completed</td>
                                </tr>
                              </tbody>
                            </Table>
                          </div>
                        </Col>
                      </Row>
                  
                      {/* Item List Section */}
                      <Row className="mb-4">
                        <Col>
                          <div className="item-list">
                            <h3>Items Ordered</h3>
                            <Table responsive bordered className="order-table">
                              <thead>
                                <tr>
                                  <th>Item</th>
                                  <th>Price</th>
                                  <th>Quantity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orderInfo.items.map((item, index) => (
                                  <tr key={index}>
                                    <td>{item.name}</td>
                                    <td>${item.price}</td>
                                    <td>{item.quantity}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Col>
                      </Row>
                  
                      {/* Shipping Address Section */}
                      <Row className="mb-4">
                        <Col>
                          <div className="shipping-address">
                            <h3>Shipping Address</h3>
                            <p>{orderInfo.customer.shippingAddress.line1}</p>
                            <p>{orderInfo.customer.shippingAddress.city}, {orderInfo.customer.shippingAddress.state} {orderInfo.customer.shippingAddress.postal_code}</p>
                            <p>{orderInfo.customer.shippingAddress.country}</p>
                          </div>
                        </Col>
                      </Row>
                  
                      {/* Payment Info Section */}
                      <Row className="mb-4">
                        <Col>
                          <div className="payment-info">
                            <h3>Payment Information</h3>
                            <p><strong>Payment Status:</strong> {orderInfo.paymentStatus}</p>
                            <p><strong>Amount:</strong> ${orderInfo.amountTotal}</p>
                          </div>
                        </Col>
                      </Row>
                  
                      {/* Continue Shopping Button */}
                      <Row className="text-center">
                        <Col>
                          <Button color="primary" href="/store">
                            Continue Shopping
                          </Button>
                        </Col>
                      </Row>
                  
                      {/* Additional Instructions Section */}
                      <Row className="mt-4 text-center">
                        <Col>
                        <p>If you have any questions about your order, please contact our support team at <a href="mailto:info@zipquill.com" className="email-link">info@zipquill.com</a>.</p>                     
                        </Col>
                      </Row>
                    </Container>
                  )}
                </Col>
              </Row>
            </Container>
            <AuthFooter />
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderSuccess;
